/**
 * Template Management Reducer
 * NgRx reducer for admin template state management
 */
import { createReducer, on } from '@ngrx/store';
import {
  Template,
  TemplateStats,
  TemplateFilters,
  TemplateVersion,
  JsonValidationResponse
} from '../../models/template.models';
import * as TemplateActions from './template.actions';

/**
 * Template State Interface
 */
export interface TemplateState {
  templates: Template[];
  total: number;
  page: number;
  pageSize: number;
  stats: TemplateStats | null;
  selectedTemplate: Template | null;
  selectedTemplateVersions: TemplateVersion[];
  filters: TemplateFilters;
  loading: boolean;
  statsLoading: boolean;
  detailLoading: boolean;
  saving: boolean;
  error: string | null;
  statsError: string | null;
  detailError: string | null;
  validation: JsonValidationResponse | null;
  validating: boolean;
  lastUpdated: string | null;
}

/**
 * Initial State
 */
export const initialState: TemplateState = {
  templates: [],
  total: 0,
  page: 1,
  pageSize: 20,
  stats: null,
  selectedTemplate: null,
  selectedTemplateVersions: [],
  filters: {},
  loading: false,
  statsLoading: false,
  detailLoading: false,
  saving: false,
  error: null,
  statsError: null,
  detailError: null,
  validation: null,
  validating: false,
  lastUpdated: null
};

/**
 * Template Reducer
 */
export const templateReducer = createReducer(
  initialState,

  // Load Templates
  on(TemplateActions.loadTemplates, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(TemplateActions.loadTemplatesSuccess, (state, { response }) => ({
    ...state,
    templates: response.templates,
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
    loading: false,
    lastUpdated: new Date().toISOString()
  })),
  on(TemplateActions.loadTemplatesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load Template Stats
  on(TemplateActions.loadTemplateStats, (state) => ({
    ...state,
    statsLoading: true,
    statsError: null
  })),
  on(TemplateActions.loadTemplateStatsSuccess, (state, { stats }) => ({
    ...state,
    stats,
    statsLoading: false
  })),
  on(TemplateActions.loadTemplateStatsFailure, (state, { error }) => ({
    ...state,
    statsLoading: false,
    statsError: error
  })),

  // Load Single Template
  on(TemplateActions.loadTemplate, (state) => ({
    ...state,
    detailLoading: true,
    detailError: null
  })),
  on(TemplateActions.loadTemplateSuccess, (state, { response }) => ({
    ...state,
    selectedTemplate: response.template,
    selectedTemplateVersions: response.versions || [],
    detailLoading: false
  })),
  on(TemplateActions.loadTemplateFailure, (state, { error }) => ({
    ...state,
    detailLoading: false,
    detailError: error
  })),

  // Create Template
  on(TemplateActions.createTemplate, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(TemplateActions.createTemplateSuccess, (state, { template }) => ({
    ...state,
    templates: [template, ...state.templates],
    total: state.total + 1,
    saving: false
  })),
  on(TemplateActions.createTemplateFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Update Template
  on(TemplateActions.updateTemplate, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(TemplateActions.updateTemplateSuccess, (state, { template }) => ({
    ...state,
    templates: state.templates.map(t => t.id === template.id ? template : t),
    selectedTemplate: state.selectedTemplate?.id === template.id ? template : state.selectedTemplate,
    saving: false
  })),
  on(TemplateActions.updateTemplateFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Delete Template
  on(TemplateActions.deleteTemplate, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(TemplateActions.deleteTemplateSuccess, (state, { templateId }) => ({
    ...state,
    templates: state.templates.filter(t => t.id !== templateId),
    total: state.total - 1,
    selectedTemplate: state.selectedTemplate?.id === templateId ? null : state.selectedTemplate,
    saving: false
  })),
  on(TemplateActions.deleteTemplateFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Clone Template
  on(TemplateActions.cloneTemplate, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(TemplateActions.cloneTemplateSuccess, (state, { template }) => ({
    ...state,
    templates: [template, ...state.templates],
    total: state.total + 1,
    saving: false
  })),
  on(TemplateActions.cloneTemplateFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Approve/Reject Template
  on(TemplateActions.approveTemplate, TemplateActions.rejectTemplate, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(TemplateActions.approvalSuccess, (state, { template }) => ({
    ...state,
    templates: state.templates.map(t => t.id === template.id ? template : t),
    selectedTemplate: state.selectedTemplate?.id === template.id ? template : state.selectedTemplate,
    saving: false
  })),
  on(TemplateActions.approvalFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Validate JSON
  on(TemplateActions.validateJson, (state) => ({
    ...state,
    validating: true,
    validation: null
  })),
  on(TemplateActions.validateJsonSuccess, (state, { response }) => ({
    ...state,
    validation: response,
    validating: false
  })),
  on(TemplateActions.validateJsonFailure, (state, { error }) => ({
    ...state,
    validating: false,
    error
  })),

  // UI State
  on(TemplateActions.selectTemplate, (state, { templateId }) => ({
    ...state,
    selectedTemplate: templateId ? state.templates.find(t => t.id === templateId) || null : null
  })),
  on(TemplateActions.setFilters, (state, { filters }) => ({
    ...state,
    filters
  })),
  on(TemplateActions.clearError, (state) => ({
    ...state,
    error: null,
    statsError: null,
    detailError: null
  })),
  on(TemplateActions.clearValidation, (state) => ({
    ...state,
    validation: null
  })),
  on(TemplateActions.clearSelectedTemplate, (state) => ({
    ...state,
    selectedTemplate: null,
    selectedTemplateVersions: []
  }))
);

